import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Plus, Search, Boxes, ChevronRight, ShieldCheck, ShieldX,
  AlertTriangle, Filter, Clock, Wrench, FileText, Upload,
  Loader2, CheckCircle2, XCircle, Check, X
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const WarrantyBadge = ({ status, daysRemaining }) => {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-[#142915] text-[#00E01A] border border-[#00E01A]/20">
        <ShieldCheck size={12} />
        Sous garantie
      </span>
    );
  }
  if (status === 'expiring_soon') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-[#2A2010] text-[#F39C12] border border-[#F39C12]/20">
        <AlertTriangle size={12} />
        {daysRemaining}j restants
      </span>
    );
  }
  if (status === 'expired') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-[#2A1515] text-[#E74C3C] border border-[#E74C3C]/20">
        <ShieldX size={12} />
        Expirée
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-[#2C3338] text-[#8C8F94]">
      Inconnu
    </span>
  );
};

const statusLabels = {
  in_stock: 'En stock',
  sold: 'Vendu',
  demo: 'Démo',
  in_sav: 'En SAV',
  on_loan: 'En prêt',
  destroyed: 'Détruit'
};

const statusColors = {
  in_stock: 'bg-[#152A35] text-[#3498DB]',
  sold: 'bg-[#142915] text-[#00E01A]',
  demo: 'bg-[#2A2010] text-[#F39C12]',
  in_sav: 'bg-[#2A1515] text-[#E74C3C]',
  on_loan: 'bg-[#2A2010] text-[#F39C12]',
  destroyed: 'bg-[#2C3338] text-[#8C8F94]'
};

const EquipmentForm = ({ products, clients, franchises, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    product_id: '',
    client_id: '',
    serial_number: '',
    purchase_date: '',
    delivery_date: '',
    warranty_months: 24,
    franchise_id: franchises[0]?.id || '',
    status: 'sold'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Produit *</Label>
          <Select
            value={formData.product_id}
            onValueChange={(value) => setFormData({ ...formData, product_id: value })}
          >
            <SelectTrigger data-testid="equipment-product-select">
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent className="max-h-[200px]">
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} ({p.reference})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Client *</Label>
          <Select
            value={formData.client_id}
            onValueChange={(value) => setFormData({ ...formData, client_id: value })}
          >
            <SelectTrigger data-testid="equipment-client-select">
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent className="max-h-[200px]">
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>N° de série</Label>
          <Input
            value={formData.serial_number}
            onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
            placeholder="Optionnel"
            className="font-mono"
            data-testid="equipment-serial-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Franchise *</Label>
          <Select
            value={formData.franchise_id}
            onValueChange={(value) => setFormData({ ...formData, franchise_id: value })}
          >
            <SelectTrigger data-testid="equipment-franchise-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {franchises.map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Date d'achat</Label>
          <Input
            type="date"
            value={formData.purchase_date}
            onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
            data-testid="equipment-purchase-date-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Date de livraison</Label>
          <Input
            type="date"
            value={formData.delivery_date}
            onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
            data-testid="equipment-delivery-date-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Garantie (mois)</Label>
          <Input
            type="number"
            value={formData.warranty_months}
            onChange={(e) => setFormData({ ...formData, warranty_months: parseInt(e.target.value) || 24 })}
            data-testid="equipment-warranty-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Statut</Label>
          <Select
            value={formData.status}
            onValueChange={(value) => setFormData({ ...formData, status: value })}
          >
            <SelectTrigger data-testid="equipment-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="in_stock">En stock</SelectItem>
              <SelectItem value="sold">Vendu</SelectItem>
              <SelectItem value="demo">Démo</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-[#2C3338]">
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" className="bg-[#00E01A] hover:bg-[#00B815] text-black">
          Créer
        </Button>
      </div>
    </form>
  );
};

const ImportPdfDialog = ({ api, clients, franchises, onImported, onClose }) => {
  const [step, setStep] = useState('upload'); // upload, analyzing, preview, importing, done
  const [file, setFile] = useState(null);
  const [clientId, setClientId] = useState('');
  const [franchiseId, setFranchiseId] = useState(franchises[0]?.id || '');
  const [status, setStatus] = useState('sold');
  const [extractedData, setExtractedData] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});

  const handleUpload = async () => {
    if (!file) { toast.error('Sélectionnez un fichier PDF'); return; }
    
    setStep('analyzing');
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/inventory/import-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      
      setExtractedData(response.data);
      
      // Auto-select all matched items
      const selections = {};
      response.data.products.forEach((p, idx) => {
        if (p.catalog_match) {
          selections[idx] = true;
        }
      });
      setSelectedItems(selections);
      setStep('preview');
    } catch (error) {
      console.error('PDF import error:', error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'analyse du PDF");
      setStep('upload');
    }
  };

  const toggleItem = (idx) => {
    setSelectedItems(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleConfirm = async () => {
    const items = extractedData.products
      .map((p, idx) => {
        if (!selectedItems[idx] || !p.catalog_match) return null;
        return {
          product_id: p.catalog_match.product_id,
          name: p.catalog_match.product_name,
          quantity: p.quantity,
          serial_numbers: p.serial_numbers,
          unit_price: p.unit_price,
        };
      })
      .filter(Boolean);

    if (items.length === 0) {
      toast.error('Aucun produit sélectionné');
      return;
    }

    setStep('importing');
    try {
      const response = await api.post('/inventory/import-confirm', {
        items,
        client_id: clientId || null,
        franchise_id: franchiseId,
        order_date: extractedData.order_date,
        status,
      });
      
      toast.success(`${response.data.created} équipement(s) importé(s)`);
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} erreur(s)`);
      }
      setStep('done');
      onImported();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
      setStep('preview');
    }
  };

  const selectedCount = Object.values(selectedItems).filter(Boolean).length;
  const totalEquipments = extractedData?.products
    .filter((_, idx) => selectedItems[idx])
    .reduce((sum, p) => sum + p.quantity, 0) || 0;

  if (step === 'done') {
    return (
      <div className="text-center py-8 space-y-4">
        <CheckCircle2 size={56} className="mx-auto text-[#00E01A]" />
        <h3 className="text-xl font-bold text-white">Import terminé</h3>
        <p className="text-[#8C8F94]">Les équipements ont été ajoutés à l'inventaire.</p>
        <Button onClick={onClose} className="bg-[#00E01A] hover:bg-[#00B815] text-black">Fermer</Button>
      </div>
    );
  }

  if (step === 'analyzing') {
    return (
      <div className="text-center py-12 space-y-4">
        <Loader2 size={48} className="mx-auto text-[#00E01A] animate-spin" />
        <h3 className="text-lg font-semibold text-white">Analyse IA en cours...</h3>
        <p className="text-[#8C8F94] text-sm">Claude analyse votre document et identifie les produits commandés</p>
      </div>
    );
  }

  if (step === 'importing') {
    return (
      <div className="text-center py-12 space-y-4">
        <Loader2 size={48} className="mx-auto text-[#00E01A] animate-spin" />
        <h3 className="text-lg font-semibold text-white">Import en cours...</h3>
        <p className="text-[#8C8F94] text-sm">Création de {totalEquipments} équipement(s) dans l'inventaire</p>
      </div>
    );
  }

  if (step === 'preview' && extractedData) {
    return (
      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        {/* Order info header */}
        <div className="bg-[#141617] rounded-lg p-3 border border-[#2C3338] grid grid-cols-3 gap-3 text-sm">
          <div>
            <p className="text-[#8C8F94] text-xs">Fournisseur</p>
            <p className="text-white font-medium">{extractedData.supplier_name || 'Non détecté'}</p>
          </div>
          <div>
            <p className="text-[#8C8F94] text-xs">N° commande</p>
            <p className="text-white font-medium">{extractedData.order_reference || '-'}</p>
          </div>
          <div>
            <p className="text-[#8C8F94] text-xs">Date</p>
            <p className="text-white font-medium">{extractedData.order_date || '-'}</p>
          </div>
        </div>

        {/* Assignment fields */}
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Client (optionnel)</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="h-9 text-sm" data-testid="import-client-select">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                <SelectItem value="none">Aucun (stock)</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Franchise</Label>
            <Select value={franchiseId} onValueChange={setFranchiseId}>
              <SelectTrigger className="h-9 text-sm" data-testid="import-franchise-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {franchises.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Statut</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9 text-sm" data-testid="import-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sold">Vendu</SelectItem>
                <SelectItem value="in_stock">En stock</SelectItem>
                <SelectItem value="demo">Démo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Products preview table */}
        <div className="bg-[#141617] rounded-lg border border-[#2C3338] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#2C3338]">
                <th className="p-2 w-8"></th>
                <th className="text-left p-2 text-[#8C8F94] text-xs font-medium">Réf. extraite</th>
                <th className="text-left p-2 text-[#8C8F94] text-xs font-medium">Produit catalogue</th>
                <th className="text-center p-2 text-[#8C8F94] text-xs font-medium">Qté</th>
                <th className="text-left p-2 text-[#8C8F94] text-xs font-medium">N° série</th>
                <th className="text-right p-2 text-[#8C8F94] text-xs font-medium">Prix</th>
                <th className="text-center p-2 text-[#8C8F94] text-xs font-medium">Match</th>
              </tr>
            </thead>
            <tbody>
              {extractedData.products.map((p, idx) => {
                const hasMatch = !!p.catalog_match;
                const isSelected = !!selectedItems[idx];
                return (
                  <tr
                    key={idx}
                    className={`border-b border-[#2C3338] last:border-0 transition-colors ${
                      isSelected ? 'bg-[#00E01A]/5' : hasMatch ? 'hover:bg-[#1D2327]' : 'opacity-50'
                    }`}
                  >
                    <td className="p-2 text-center">
                      {hasMatch && (
                        <button
                          onClick={() => toggleItem(idx)}
                          className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${
                            isSelected
                              ? 'bg-[#00E01A] border-[#00E01A] text-black'
                              : 'border-[#52555A] text-transparent hover:border-[#8C8F94]'
                          }`}
                          data-testid={`import-toggle-${idx}`}
                        >
                          <Check size={12} />
                        </button>
                      )}
                    </td>
                    <td className="p-2">
                      <p className="font-mono text-white text-xs">{p.extracted_reference || '-'}</p>
                      <p className="text-[#8C8F94] text-xs truncate max-w-[150px]">{p.extracted_name}</p>
                    </td>
                    <td className="p-2">
                      {p.catalog_match ? (
                        <div>
                          <p className="text-white text-xs font-medium">{p.catalog_match.product_name}</p>
                          <p className="text-[#8C8F94] text-xs font-mono">{p.catalog_match.product_ref}</p>
                        </div>
                      ) : (
                        <span className="text-[#E74C3C] text-xs">Non trouvé dans le catalogue</span>
                      )}
                    </td>
                    <td className="p-2 text-center text-white font-medium">{p.quantity}</td>
                    <td className="p-2">
                      {p.serial_numbers.filter(Boolean).length > 0 ? (
                        <div className="space-y-0.5">
                          {p.serial_numbers.filter(Boolean).map((sn, si) => (
                            <p key={si} className="font-mono text-[#00E01A] text-xs">{sn}</p>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[#F39C12] text-xs italic">Aucun</span>
                      )}
                    </td>
                    <td className="p-2 text-right text-white text-xs">
                      {p.unit_price ? `${p.unit_price.toLocaleString()}€` : '-'}
                    </td>
                    <td className="p-2 text-center">
                      {hasMatch ? (
                        <span className={`inline-flex items-center gap-1 text-xs font-medium ${
                          p.catalog_match.match_score >= 80 ? 'text-[#00E01A]' : 'text-[#F39C12]'
                        }`}>
                          <CheckCircle2 size={14} />
                          {p.catalog_match.match_score}%
                        </span>
                      ) : (
                        <XCircle size={14} className="text-[#E74C3C] mx-auto" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Summary + confirm */}
        <div className="flex items-center justify-between pt-3 border-t border-[#2C3338]">
          <p className="text-[#8C8F94] text-sm">
            <span className="text-white font-semibold">{selectedCount}</span> produit(s) sélectionné(s)
            {' '} = <span className="text-[#00E01A] font-semibold">{totalEquipments}</span> équipement(s) à créer
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setStep('upload'); setExtractedData(null); }}>
              Recommencer
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={selectedCount === 0}
              className="bg-[#00E01A] hover:bg-[#00B815] text-black"
              data-testid="import-confirm-btn"
            >
              <Check size={16} className="mr-2" />
              Importer {totalEquipments} équipement(s)
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Upload step
  return (
    <div className="space-y-4">
      <p className="text-[#8C8F94] text-sm">
        Importez un bon de commande ou une facture fournisseur au format PDF.
        L'IA analysera le document et identifiera les produits pour les ajouter à l'inventaire.
      </p>

      <div
        className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${
          file ? 'border-[#00E01A] bg-[#00E01A]/5' : 'border-[#2C3338] hover:border-[#00E01A]/50'
        }`}
        onClick={() => document.getElementById('pdf-import-input')?.click()}
      >
        <input
          type="file"
          accept=".pdf"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="hidden"
          id="pdf-import-input"
          data-testid="pdf-import-input"
        />
        {file ? (
          <div className="flex items-center justify-center gap-3 text-[#00E01A]">
            <FileText size={28} />
            <div className="text-left">
              <p className="font-medium">{file.name}</p>
              <p className="text-xs text-[#8C8F94]">{(file.size / 1024).toFixed(0)} Ko</p>
            </div>
          </div>
        ) : (
          <>
            <Upload size={36} className="mx-auto text-[#8C8F94] mb-3" />
            <p className="text-[#8C8F94] font-medium">Cliquez pour sélectionner un PDF</p>
            <p className="text-[#52555A] text-xs mt-1">Bon de commande, facture, liste de livraison...</p>
          </>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-[#2C3338]">
        <Button variant="outline" onClick={onClose}>Annuler</Button>
        <Button
          onClick={handleUpload}
          disabled={!file}
          className="bg-[#00E01A] hover:bg-[#00B815] text-black"
          data-testid="pdf-analyze-btn"
        >
          <FileText size={16} className="mr-2" />
          Analyser avec IA
        </Button>
      </div>
    </div>
  );
};

const Inventory = () => {
  const { api, isSuperAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [equipment, setEquipment] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [warrantyFilter, setWarrantyFilter] = useState(searchParams.get('warranty_status') || 'all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const fetchData = async () => {
    try {
      const [eqRes, prodRes, clientRes, franchiseRes] = await Promise.all([
        api.get('/equipment'),
        api.get('/products'),
        api.get('/clients'),
        api.get('/franchises')
      ]);
      setEquipment(eqRes.data);
      setProducts(prodRes.data);
      setClients(clientRes.data);
      setFranchises(franchiseRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  const handleSave = async (data) => {
    try {
      await api.post('/equipment', data);
      toast.success('Équipement créé');
      fetchData();
      setDialogOpen(false);
    } catch (error) {
      console.error('Failed to save equipment:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const filteredEquipment = equipment.filter(eq => {
    const matchesSearch = 
      eq.serial_number?.toLowerCase().includes(search.toLowerCase()) ||
      eq.product_name?.toLowerCase().includes(search.toLowerCase()) ||
      eq.client_name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || eq.status === statusFilter;
    const matchesWarranty = warrantyFilter === 'all' || eq.warranty_status === warrantyFilter;
    const matchesRevision = searchParams.get('revision_due') !== 'true' || eq.revision_due === true;
    return matchesSearch && matchesStatus && matchesWarranty && matchesRevision;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] animate-pulse">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="p-4 border-b border-[#2C3338] last:border-0">
              <div className="h-4 w-48 bg-[#2C3338] rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">
            Inventaire
          </h1>
          <p className="text-[#8C8F94] mt-1">
            {equipment.length} équipement{equipment.length > 1 ? 's' : ''} enregistré{equipment.length > 1 ? 's' : ''}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <button
                className="flex items-center gap-2 bg-[#1D2327] hover:bg-[#2C3338] text-white font-medium px-4 py-2.5 rounded-full border border-[#2C3338] transition-all"
                data-testid="import-pdf-btn"
              >
                <FileText size={18} />
                <span className="hidden md:inline">Import PDF</span>
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-4xl max-h-[90vh]">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold font-['Outfit']">
                  Importer une commande fournisseur
                </DialogTitle>
              </DialogHeader>
              <ImportPdfDialog
                api={api}
                clients={clients}
                franchises={franchises}
                onImported={() => { setImportDialogOpen(false); fetchData(); }}
                onClose={() => setImportDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button
                className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-4 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)]"
                data-testid="add-equipment-btn"
              >
                <Plus size={18} />
                <span className="hidden md:inline">Ajouter</span>
              </button>
            </DialogTrigger>
          <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Outfit']">
                Nouvel équipement
              </DialogTitle>
            </DialogHeader>
            <EquipmentForm
              products={products}
              clients={clients}
              franchises={franchises}
              onSave={handleSave}
              onClose={() => setDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8F94]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par N° série, produit, client..."
            className="w-full bg-[#1D2327] border border-[#2C3338] rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-[#52555A] focus:outline-none focus:border-[#00E01A] transition-colors"
            data-testid="inventory-search-input"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-40 bg-[#1D2327] border-[#2C3338]">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent className="bg-[#1D2327] border-[#2C3338]">
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="sold">Vendus</SelectItem>
            <SelectItem value="in_stock">En stock</SelectItem>
            <SelectItem value="in_sav">En SAV</SelectItem>
            <SelectItem value="on_loan">En prêt</SelectItem>
            <SelectItem value="demo">Démo</SelectItem>
          </SelectContent>
        </Select>

        <Select value={warrantyFilter} onValueChange={setWarrantyFilter}>
          <SelectTrigger className="w-full md:w-48 bg-[#1D2327] border-[#2C3338]">
            <SelectValue placeholder="Garantie" />
          </SelectTrigger>
          <SelectContent className="bg-[#1D2327] border-[#2C3338]">
            <SelectItem value="all">Toutes garanties</SelectItem>
            <SelectItem value="active">Sous garantie</SelectItem>
            <SelectItem value="expiring_soon">Expire bientôt</SelectItem>
            <SelectItem value="expired">Expirée</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Equipment Table */}
      {filteredEquipment.length === 0 ? (
        <div className="text-center py-12">
          <Boxes size={48} className="mx-auto text-[#8C8F94] mb-4" />
          <p className="text-[#8C8F94]">Aucun équipement trouvé</p>
        </div>
      ) : (
        <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2C3338]">
                  <th className="text-left p-4 text-[#8C8F94] text-xs font-semibold uppercase tracking-wider">N° Série</th>
                  <th className="text-left p-4 text-[#8C8F94] text-xs font-semibold uppercase tracking-wider">Produit</th>
                  <th className="text-left p-4 text-[#8C8F94] text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Client</th>
                  <th className="text-left p-4 text-[#8C8F94] text-xs font-semibold uppercase tracking-wider">Statut</th>
                  <th className="text-left p-4 text-[#8C8F94] text-xs font-semibold uppercase tracking-wider">Garantie</th>
                  <th className="text-left p-4 text-[#8C8F94] text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Révision</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {filteredEquipment.map((eq) => (
                  <tr key={eq.id} className="border-b border-[#2C3338] last:border-0 hover:bg-[#141617]/50 transition-colors">
                    <td className="p-4">
                      <span className="font-mono text-white text-sm">
                        {eq.serial_number || <span className="text-[#F39C12]">À compléter</span>}
                      </span>
                    </td>
                    <td className="p-4">
                      <p className="text-white font-medium">{eq.product_name}</p>
                      <p className="text-[#8C8F94] text-xs">{eq.supplier_name}</p>
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      <Link 
                        to={`/clients/${eq.client_id}`}
                        className="text-[#8C8F94] hover:text-[#00E01A] transition-colors"
                      >
                        {eq.client_name}
                      </Link>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[eq.status]}`}>
                        {statusLabels[eq.status]}
                      </span>
                    </td>
                    <td className="p-4">
                      <WarrantyBadge
                        status={eq.warranty_status}
                        daysRemaining={eq.warranty_days_remaining}
                      />
                    </td>
                    <td className="p-4 hidden lg:table-cell">
                      {eq.revision_due ? (
                        <span className="inline-flex items-center gap-1 text-[#F39C12] text-xs">
                          <Clock size={12} />
                          À planifier
                        </span>
                      ) : eq.next_revision_date ? (
                        <span className="text-[#8C8F94] text-xs">
                          {new Date(eq.next_revision_date).toLocaleDateString('fr-FR')}
                        </span>
                      ) : (
                        <span className="text-[#52555A] text-xs">-</span>
                      )}
                    </td>
                    <td className="p-4">
                      <Link
                        to={`/sav/new?equipment_id=${eq.id}`}
                        className="p-2 rounded-lg text-[#8C8F94] hover:text-[#00E01A] hover:bg-[#00E01A]/10 transition-colors inline-flex"
                        title="Créer ticket SAV"
                      >
                        <Wrench size={16} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
